import { Bell, Calendar, LayoutGrid, LogOut, LucideIcon, Menu, Search, Star } from "lucide-react"
import { useState } from "react"
import { NavLink, Outlet, ScrollRestoration, useLocation } from "react-router-dom"
import { useLogout } from "~/auth/use-logout"
import { useUiAccess } from "~/auth/use-ui-access"
import { useViewerMaybe } from "~/auth/use-viewer"
import { cn } from "~/common/cn"
import {
  accountSettingsPath,
  dashboardPath,
  internalAdminDashboardPath,
  rootPath,
  watchlistPath,
} from "~/common/paths"
import { Sheet, SheetContent, SheetTitle } from "~/ui/sheet"

type NavItem = {
  label: string
  to: string
  icon: LucideIcon
  emoji?: string
  disabled?: boolean
  comingSoon?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { label: "Dashboard", to: dashboardPath({}), icon: LayoutGrid, emoji: "🕹️" },
  { label: "Watchlist", to: watchlistPath({}), icon: Star, emoji: "🔍" },
  {
    label: "Calendar",
    to: "#calendar",
    icon: Calendar,
    disabled: true,
    comingSoon: true,
    emoji: "📅",
  },
]

const NavItem = ({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) => {
  const Icon = item.icon
  const iconEl = item.emoji ? (
    <span className="text-sm leading-none">{item.emoji}</span>
  ) : (
    <Icon size={14} />
  )
  if (item.disabled) {
    return (
      <li>
        <span className="flex items-center gap-3 px-4 py-2.5 text-[13px] text-stone-300 cursor-default select-none">
          {iconEl}
          {item.label}
          {item.comingSoon && (
            <span className="text-[9px] font-semibold tracking-wide text-stone-300 ml-1">
              Coming Soon
            </span>
          )}
        </span>
      </li>
    )
  }
  return (
    <li>
      <NavLink
        to={item.to}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-3 px-4 py-2.5 text-[13px] transition-colors",
            isActive
              ? "bg-stone-900 text-stone-50"
              : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
          )
        }
      >
        {iconEl}
        {item.label}
      </NavLink>
    </li>
  )
}

const NavList = ({
  onNavigate,
  showNavLabel = true,
  onOpenRadarSettings,
}: {
  onNavigate?: () => void
  showNavLabel?: boolean
  onOpenRadarSettings?: () => void
}) => {
  const logout = useLogout()
  const { uiAccess } = useUiAccess()

  return (
    <>
      <nav className="flex-1 py-4 px-2">
        {showNavLabel && (
          <p className="px-2 mb-2 text-[9px] font-semibold tracking-[3px] uppercase text-stone-400">
            Navigation
          </p>
        )}
        <ul className="space-y-0.5">
          {NAV_ITEMS.slice(0, 2).map((item) => (
            <NavItem key={item.label} item={item} onNavigate={onNavigate} />
          ))}
          {onOpenRadarSettings && (
            <li>
              <button
                onClick={onOpenRadarSettings}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-[13px] text-stone-600 hover:bg-stone-100 hover:text-stone-900 transition-colors"
              >
                <span className="text-sm leading-none">ᯤ</span>
                Search Settings
              </button>
            </li>
          )}
          {NAV_ITEMS.slice(2).map((item) => (
            <NavItem key={item.label} item={item} onNavigate={onNavigate} />
          ))}
          {uiAccess?.canInternalAdmin.value && (
            <li>
              <NavLink
                to={internalAdminDashboardPath({})}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-4 py-2.5 text-[13px] transition-colors",
                    isActive
                      ? "bg-stone-900 text-stone-50"
                      : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                  )
                }
              >
                <LayoutGrid size={14} />
                Admin
              </NavLink>
            </li>
          )}
        </ul>
      </nav>
      <div className="border-t border-stone-200">
        <button
          onClick={() => {
            logout()
            onNavigate?.()
          }}
          className="flex w-full items-center gap-3 px-4 py-3 text-[13px] text-stone-500 hover:bg-stone-100 hover:text-stone-900 transition-colors"
        >
          <LogOut size={14} />
          Sign Out
        </button>
      </div>
    </>
  )
}

export const SidebarLayout = () => {
  const { viewer } = useViewerMaybe()
  const [navOpen, setNavOpen] = useState(false)
  const location = useLocation()
  const isOnDashboard = location.pathname === dashboardPath({})

  return (
    <div className="flex w-full min-h-screen">
      {/* Desktop sidebar */}
      <div className="hidden md:flex w-[220px] shrink-0 flex-col bg-stone-50 border-r border-stone-200">
        <div className="px-5 pt-6 pb-5 border-b border-stone-200">
          <span
            className="text-sm font-bold tracking-[0.15em] uppercase text-stone-900"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            SCOUT RADAR
          </span>
        </div>
        <NavList />
      </div>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-10 flex items-center px-4 py-3 bg-white border-b border-stone-200">
        <NavLink
          to={rootPath({})}
          className="flex-1 text-base font-bold text-stone-900"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          SCOUT RADAR
        </NavLink>
        <button
          onClick={() => setNavOpen(true)}
          className="ml-auto text-stone-600 hover:text-stone-900 transition-colors"
          aria-label="Open navigation"
        >
          <Menu size={20} />
        </button>
      </div>

      {/* Mobile nav drawer */}
      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent side="left" className="p-0 flex flex-col w-[280px]">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <div className="px-5 pt-6 pb-5 border-b border-stone-200">
            <span
              className="text-sm font-bold tracking-[0.15em] uppercase text-stone-900"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              SCOUT RADAR
            </span>
          </div>
          <NavList
            onNavigate={() => setNavOpen(false)}
            showNavLabel={false}
            onOpenRadarSettings={
              isOnDashboard
                ? () => {
                    window.dispatchEvent(new CustomEvent("open-radar-settings"))
                    setNavOpen(false)
                  }
                : undefined
            }
          />
        </SheetContent>
      </Sheet>

      {/* Main content */}
      <div className="flex min-w-0 flex-1 flex-col overflow-x-hidden md:mt-0 mt-[49px]">
        {/* Top bar */}
        <div className="hidden md:flex h-11 shrink-0 items-center border-b border-stone-200 bg-white px-5 gap-3">
          <div className="flex flex-1 items-center gap-2 rounded border border-stone-200 bg-stone-50 px-3 py-1.5 max-w-xs">
            <Search size={12} className="text-stone-400 shrink-0" />
            <input
              type="text"
              placeholder="Keywords, maker, style…"
              className="flex-1 bg-transparent text-[12px] text-stone-700 placeholder:text-stone-400 outline-none"
            />
          </div>
          <div className="flex-1" />
          <button
            className="text-stone-400 hover:text-stone-700 transition-colors"
            aria-label="Notifications"
          >
            <Bell size={15} />
          </button>
          {viewer && (
            <NavLink
              to={accountSettingsPath({})}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              {viewer.avatarThumbUrl ? (
                <img
                  src={viewer.avatarThumbUrl}
                  alt={viewer.firstName ?? ""}
                  className="h-7 w-7 rounded-full object-cover"
                />
              ) : (
                <div className="h-7 w-7 rounded-full bg-stone-900 flex items-center justify-center text-[10px] font-bold text-stone-50">
                  {viewer.firstName?.[0]?.toUpperCase() ?? "U"}
                </div>
              )}
              <span className="text-[12px] text-stone-700">{viewer.firstName}</span>
            </NavLink>
          )}
        </div>

        <Outlet />
        <ScrollRestoration />
      </div>
    </div>
  )
}
